import React, { useContext, useState } from "react";
import Paper from "@/components/paper/Paper";
import DataMaskingParagraph from "@/components/dataMaskingParagraph/DataMaskingParagraph";
import Reliability from "@/app/pages/companyProfile/financial/Reliability";
import { useTranslation } from "react-i18next";
import { IDataMaskingParagraphData } from "@/components/dataMaskingParagraph/IDataMaskingParagraph";
import { checkSupression } from "@/utils/index";
import { usePageURL } from "@/shared/customHooks";
import { isNotUndefinedOrNull, setTabNameInDigitalData } from "@/shared/FunctionCore";
import { AppContext, ContextType } from "@/shared/context";
import Cashflow from "@/app/pages/companyProfile/financial/tabs/Cashflow";
import AssetTables from "@/app/pages/companyProfile/financial/tabs/AssetsTable";
import Liabilities from "@/app/pages/companyProfile/financial/tabs/Liabilities";
import Stakeholder from "@/app/pages/companyProfile/financial/tabs/Stakeholder";
import RevenueChartData from "@/app/pages/companyProfile/financial/tabs/RevenueChartData";

const Financial = ({ props }): JSX.Element => {
  const { t } = useTranslation();
  const { countryCode, dunsNumber } = usePageURL();
  const { isUnitedStates, isEuBd } = useContext(AppContext) as ContextType;
  const [activeTab, setActiveTab] = useState<number>(0);
  interface FinancialSection {
    component: JSX.Element;
  }

  let financialSections: FinancialSection[] = [];
  if (props.cashFlows) {
    financialSections = [
      {
        component: <Cashflow props={props} />,
      },
      {
        component: <AssetTables props={props} />,
      },
      {
        component: <Liabilities props={props} />,
      },
      {
        component: <Stakeholder props={props} />,
      },
    ];
  }

  const handleTabClick = (index: number, tabName: string) => {
    setActiveTab(index);
    setTabNameInDigitalData(tabName);
  };

  const maskedFinancialData: IDataMaskingParagraphData = {
    maskHeadingLocalized: t("companyProfile.financial.maskHeadingLocalized"),
    maskContentLocalized: t("companyProfile.financial.maskContentLocalized"),
    hooversFreeTrialDirectoryPathRewritten:
      countryCode == "de" || countryCode == "at"
        ? `${t(
            "companyProfile.financial.hooversFreeTrialDirectoryPathRewritten"
          )}${dunsNumber}`
        : t("companyProfile.financial.hooversFreeTrialDirectoryPathRewritten"),
    maskButtonLocalized: t("companyProfile.financial.maskButtonLocalized"),
  };

  const buttonAction = () => {
    let redirection = "";
    if (countryCode == "de" || countryCode == "at") {
      redirection = `${t(
        "companyProfile.financial.getDandBCreditLink"
      )}${dunsNumber}`;
    } else {
      redirection = `${t("companyProfile.financial.getDandBCreditLink")}`;
    }
    window.location.href = redirection;
};

interface ChartRevenueDataParsed {
  acostOfRevenue: Array<{ data: { v: number | null; f: string | null }; revenue: number | null }>;
  adilutedEPS: Array<{ data: { v: number | null; f: string | null }; revenue: number | null }>;
  agrossProfit: Array<{ data: { v: number | null; f: string | null }; revenue: number | null }>;
  aincomeBeforeTax: Array<{ data: { v: number | null; f: string | null }; revenue: number | null }>;
  aincomeYear: Array<{ data: { v: number | null; f: string | null }; revenue: number | null }>;
  aoperatingIncome: Array<{ data: { v: number | null; f: string | null }; revenue: number | null }>;
  atotalNetIncome: Array<{ data: { v: number | null; f: string | null }; revenue: number | null }>;
}

const transformChartRevenueDataData = (props: any): any => {
  const sortedAnnualIncomes = props.annualIncomes ? 
    props.annualIncomes.sort((a: any, b: any) => parseInt(a.year) - parseInt(b.year)) : [];

  const years = sortedAnnualIncomes.map((income: any) => income.year);
  const parseRevenueData: ChartRevenueDataParsed = {
      acostOfRevenue: [],
      adilutedEPS: [],
      agrossProfit: [],
      aincomeBeforeTax: [],
      aincomeYear: [],
      aoperatingIncome: [],
      atotalNetIncome: []
  };

  const createDataArray = (value: (number | null)[]): Array<{ data: { v: number | null; f: string | null }; revenue: number | null }> => {
      return years.map((year, index) => ({
          data: { v: index + 1, f: year },
          revenue: value[index] !== undefined ? value[index] : null,
      }));
  };

  parseRevenueData.acostOfRevenue = createDataArray(
      sortedAnnualIncomes.map((income: any) => income.costOfRevenue ?? null)
  );
  parseRevenueData.adilutedEPS = createDataArray(
      sortedAnnualIncomes.map((income: any) => income.dilutedEPS ?? null)
  );
  parseRevenueData.agrossProfit = createDataArray(
      sortedAnnualIncomes.map((income: any) => income.grossProfit ?? null)
  );
  parseRevenueData.aincomeBeforeTax = createDataArray(
      sortedAnnualIncomes.map((income: any) => income.incomeBeforeTax ?? null)
  );
  parseRevenueData.aincomeYear = createDataArray(
      sortedAnnualIncomes.map((income: any) => income.revenue ?? null)
  );
  parseRevenueData.aoperatingIncome = createDataArray(
      sortedAnnualIncomes.map((income: any) => income.operatingIncome ?? null)
  );
  parseRevenueData.atotalNetIncome = createDataArray(
      sortedAnnualIncomes.map((income: any) => income.totalNetIncome ?? null)
  );

  return { ...props, ...parseRevenueData };
};

const ChartRevenueData = transformChartRevenueDataData(props);


  return (
    <>
      <a
        className="anchor"
        data-name="financials-anchor"
        href="/financials-anchor"
      >
        <span className="display-hidden">Financial</span>
      </a>
      <Paper additionalContainerClass={"financial-component-main-heading"} title={isUnitedStates && props.privateCompany ? t("companyProfile.financial.titleLocalizedPrivate") : t("companyProfile.financial.titleLocalized")}>
        {/* <DataMaskingParagraph dataMasking={properties}></DataMaskingParagraph> */}
        {/* <div data-sly-test="${wcmmode.edit}">
    <div data-emptytext="Configure ${component.title}" className="cq-placeholder"></div>
</div> */}

        {/* <div data-sly-use.model="com.dnb.core.models.company_profile.CompanyProfileFinancialsComponentModel"> */}
        <div>
          {/* <div data-sly-test="${model.showComponentInfo}"> */}
          {/* <script>
							if( digitalData.page.pageInfo.companyProfile==undefined ) {
                digitalData.page.pageInfo.companyProfile = {};
            }
            if( digitalData.page.pageInfo.companyProfile.financials==undefined ) {
                digitalData.page.pageInfo.companyProfile.financials=[];
            }
						</script> */}
          {/* <a className="anchor" data-name="{properties.componentid}"></a> */}
          <div
            className={`financials-component-wrapper ${t(
              "companyProfile.financial.componentclass"
            )}`}
          >
            <div className="financials-container business-directory-redesign-container ">
              <div className="row pad-lr-0">
                <div className="col-md-12 pad-lr-0">
                  {/* <div data-sly-test="${model.showHeaderInfo}"> */}
                  <div>
                    <div className="">
                      {/* <div className="row">
													<div className="col-md-12">
														<div className="title">{properties.titleLocalized ? properties.titleLocalized : props.componentHeading}</div>
													</div>
												</div> */}
                    </div>
                    <div className="sub_module_header">
                      <div className="row overview pad-lr-0">
                        <div className="col-md-8 col-sm-12 col-12">
                          <div className="description">
                            {t("companyProfile.financial.description")}
                            <a
                              className="findOutMoreDescription"
                              href={t(
                                "companyProfile.financial.findOutMoreLink"
                              )}
                            >
                              {t("companyProfile.financial.findOutMoreLabel")}
                            </a>
                          </div>
                          {/* {properties.description && !properties.descriptionLocalized &&
															<div className="description">{properties.description}</div>
														}
														{properties.descriptionLocalized &&
															<div className="description">{properties.descriptionLocalized}</div>
														} */}
                        </div>
                        <div className="col-md-4">
                          {/* {properties.linkType */}
                          <div
                            className="module_cta_financial desktop-cta-finacial-link"
                            style={{ position: "relative" }}
                          >
                            {/* <Link cta={properties.cta}></Link> */}
                            <a
                              href={
                                countryCode == "de" || countryCode == "at"
                                  ? `${t(
                                      "companyProfile.financial.getDandBCreditLink"
                                    )}${dunsNumber}`
                                  : t(
                                      "companyProfile.financial.getDandBCreditLink"
                                    )
                              }
                              aria-label="cta"
                              rel="noreferrer"
                              //  onClick="${props.cta.tracking @ context='html'}"
                              target={props?.cta?.newTab ? "_blank" : "_self"}
                            >
                              <span className="linkLabel">
                                {props?.cta?.type === "text" && (
                                  <span>
                                    <span className="icon right-unlock blue test"></span>{" "}
                                    {t(
                                      "companyProfile.financial.getDandBCreditLabel"
                                    )}
                                    <span className="icon chevron-right blue test"></span>
                                  </span>
                                )}
                              </span>
                              {props?.cta?.type === "image" && (
                                <img
                                  src={props.cta.srcUrl || ""}
                                  alt={props.cta.altText || "altText"}
                                />
                              )}
                            </a>
                            {/* <div data-sly-use.template="templates/cta.html" data-sly-call="${template.cta @cta=model.cta}" /> */}
                          </div>
                          {t("companyProfile.financial.isEu") && (
                            <div
                              className="module_cta_financial"
                              style={{ marginTop: "10px", position: "inherit" }}
                            >
                              <a
                                href={t(
                                  "companyProfile.financial.openSampleReportLink"
                                )}
                                rel="noreferrer"
                                aria-label="ctaOne"
                                className={`ext-icon`}
                                //  onClick="${props.ctaone.tracking @ context='html'}"
                                target={
                                  props?.ctaone?.newTab ? "_blank" : "_self"
                                }
                              >
                                <span className="linkLabel">
                                  {props?.ctaone?.type === "text" && (
                                    <span>
                                      {" "}
                                      {t(
                                        "companyProfile.financial.openSampleReportLabel"
                                      )}{" "}
                                      <span className="icon chevron-right blue test"></span>
                                    </span>
                                  )}
                                </span>
                                {props?.ctaone?.type === "image" && (
                                  <img
                                    src={props.ctaone.srcUrl || ""}
                                    alt={props.ctaone.altText || "altText"}
                                  />
                                )}
                              </a>
                              {/* <div data-sly-use.template="templates/ctaone.html" data-sly-call="${template.ctaone @ctaone=model.ctaone}" /> */}
                            </div>
                          )}
                        </div>
                        <br />
                      </div>
                      <div className="row pad-0">
                        <div className="row revenue pad-0 margin-0">
                          {(props.showBodyInfo || !props.inlineMasking) && (
                            <div className="col-md-12">
                              <div className="financial_period">
                                <div className="description">
                                  <p>
                                    {isNotUndefinedOrNull(props?.financialFormattedDate)
                                      ? <> 
                                          {t("companyProfile.financial.turnOverInPrefix")}
                                          {props?.financialFormattedDate} 
                                          {t("companyProfile.financial.turnOverInSuffix")}
                                        </>
                                      : t("companyProfile.financial.headingWhenFFDisNotAvailable")
                                    }
                                    {isNotUndefinedOrNull(props?.salesCurrency) && countryCode !== "hu"
                                      ? props?.salesCurrency
                                      : ""
                                    }
                                  </p>
                                </div>
                              </div>
                              <div className="rev_title">
                                {t("companyProfile.financial.annualTurnover")}{" "}
                                {isNotUndefinedOrNull(props?.annualRevenueYear)
                                  ? props?.annualRevenueYear
                                  : ""}
                              </div>
                              <div className="rev_title_number">
                                <span>
                                  {checkSupression(props.formattedSales, {
                                    buttonLabel: t(
                                      "companyProfile.overview.revenueBtnLabel"
                                    ),
                                    text: t(
                                      "companyProfile.overview.revenuePopupDescription"
                                    ),
                                    type: "revenue",
                                    buttonAction: buttonAction,
                                  })}
                                </span>

                                <span className="currency">
                                  {" "}
                                  {props.salesCurrency}{" "}
                                </span>
                                <Reliability reliability={props.reliability} />
                                {/* <div data-sly-use.template="templates/reliability-icon.html" data-sly-call="${template.reliability_icon @reliability=model.reliability, model=model, properties=properties}" /> */}
                              </div>
                              {props.exchangeRateInfo && (
                                <div className="rev_usd">
                                  {props.exchangeRateInfo}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div>
                          {!props.showBodyInfo && props.inlineMasking && (
                            <div className="col-md-12 pad-lr-0">
                              <DataMaskingParagraph
                                dataMasking={maskedFinancialData}
                              ></DataMaskingParagraph>
                              {/* <div data-sly-include="dataMaskingInlineCreditReports.jsp" /> */}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {isUnitedStates && props.showBodyInfo && (
                      <div>
                        <div className="module_body">
                          {/* <sly data-sly-use.template="templates/revenue_chart_data.html" data-sly-call="${template.revenue_chart_data @model=model, properties=properties}" /> */}
                          <div
                            className="cmp-tabbed-nav financials-dropdown"
                            id="anchor-link-example"
                            role="menubar"
                          >
                            {/* <Subnav finData={props} /> */}
                            {/* <button aria-controls="tabbed-nav" className="background-transparent" aria-expanded="false" aria-haspopup="true" id="tabbed-nav-mobile-button">Income Statement</button> */}
                            <ul
                              aria-expanded="false"
                              aria-haspopup="true"
                              aria-label="menu"
                              className="tabbed-nav clearfix tablist"
                              id="tabbed-nav-menu"
                            >
                              {props.showNavIncome && (
                                <li
                                  className={`tab background-transparent pad-0 ${
                                    activeTab === 0 ? "active" : ""
                                  }`}
                                  id="annualIncomes_tab"
                                  role="none"
                                >
                                  <button
                                    aria-controls="annualIncomes_tab_panel"
                                    aria-selected="true"
                                    role="menuitem"
                                    onClick={() =>
                                      handleTabClick(0, "Income Statement")
                                    }
                                  >
                                    Income Statement
                                  </button>
                                  {/* <script type="application/javascript">digitalData.page.pageInfo.companyProfile.financials.push("Income Statement");</script> */}
                                </li>
                              )}
                              {!props.showNavCashflow && (
                                <li
                                  className={`tab background-transparent pad-0 ${
                                    activeTab === 1 ? "active" : ""
                                  }`}
                                  id="cashflow_tab"
                                  role="none"
                                >
                                  <button
                                    aria-controls="cashflow_tab_panel"
                                    aria-selected="false"
                                    role="menuitem"
                                    onClick={() =>
                                      handleTabClick(1, "Cash Flow")
                                    }
                                  >
                                    Cash Flow
                                  </button>
                                  {/* <script type="application/javascript">digitalData.page.pageInfo.companyProfile.financials.push("Cash Flow");</script> */}
                                </li>
                              )}
                              {props.showNavAssets && (
                                <li
                                  className={`tab background-transparent pad-0 ${
                                    activeTab === 2 ? "active" : ""
                                  }`}
                                  id="assets_tab"
                                  role="none"
                                >
                                  <button
                                    aria-controls="assets_tab_panel"
                                    aria-selected="false"
                                    role="menuitem"
                                    onClick={() => handleTabClick(2, "Assets")}
                                  >
                                    Assets
                                  </button>
                                  {/* <script type="application/javascript">digitalData.page.pageInfo.companyProfile.financials.push("Assets");</script> */}
                                </li>
                              )}
                              {props.showNavLiabilities && (
                                <li
                                  className={`tab background-transparent pad-0 ${
                                    activeTab === 3 ? "active" : ""
                                  }`}
                                  id="liability_tab"
                                  role="none"
                                >
                                  <button
                                    aria-controls="liability_tab_panel"
                                    aria-selected="false"
                                    role="menuitem"
                                    onClick={() =>
                                      handleTabClick(3, "Liability")
                                    }
                                  >
                                    Liabilities
                                  </button>
                                  {/* <script type="application/javascript">digitalData.page.pageInfo.companyProfile.financials.push("${properties.title_liability}");</script> */}
                                </li>
                              )}
                              {props.showNavEquity && (
                                <li
                                  className={`tab background-transparent pad-0 ${
                                    activeTab === 4 ? "active" : ""
                                  }`}
                                  id="equity_tab"
                                  role="none"
                                >
                                  <button
                                    aria-controls="equity_tab_panel"
                                    aria-selected="false"
                                    role="menuitem"
                                    onClick={() =>
                                      handleTabClick(4, "Shareholders Equity")
                                    }
                                  >
                                    Shareholders Equity
                                  </button>
                                  {/* <script type="application/javascript">digitalData.page.pageInfo.companyProfile.financials.push("${properties.title_equity}");</script> */}
                                </li>
                              )}
                            </ul>
                            <div className="tabbed-nav-content" tabIndex={0}>
                              {props.trendTitle && (
                                <div className="trend-title">
                                  {props.trendTitle}
                                </div>
                              )}
                              <div className="financial-heading">
                                All financial statement data is in millions
                                except per share data | Source: Morningstar
                              </div>
                              {props.showNavIncome && activeTab === 0 && (
                                <div
                                  className="tab-content tab0"
                                  aria-expanded="true"
                                  aria-labelledby="annualIncomes_tab"
                                  id="annualIncomes_tab_panel"
                                  role="tabpanel"
                                >
                                  <div className="data-table dashed-table-tr alternate-rows">
                                    <RevenueChartData
                                      props={ChartRevenueData}
                                    />
                                  </div>
                                </div>
                              )}
                              {activeTab === 1 && (
                                <div
                                  aria-expanded="false"
                                  aria-labelledby="cashflow_tab"
                                  className="tab-content tab1"
                                  id="cashflow_tab_panel"
                                  role="tabpanel"
                                >
                                  <div className="data-table dashed-table-tr alternate-rows">
                                    <Cashflow props={props} />
                                  </div>
                                </div>
                              )}
                              {props.showNavAssets && activeTab === 2 && (
                                <div
                                  aria-expanded="false"
                                  aria-labelledby="assets_tab"
                                  className="tab-content tab2"
                                  id="assets_tab_panel"
                                  role="tabpanel"
                                >
                                  <div className="data-table dashed-table-tr alternate-rows">
                                    <AssetTables props={props} />
                                    {/* <sly data-sly-use.template="templates/assets_table.html" data-sly-call="${template.assets_table @model=model, properties=properties}" /> */}
                                  </div>
                                </div>
                              )}
                              {props.showNavLiabilities && activeTab === 3 && (
                                <div
                                  aria-expanded="false"
                                  aria-labelledby="liability_tab"
                                  className="tab-content tab3"
                                  id="liability_tab_panel"
                                  role="tabpanel"
                                >
                                  <div className="data-table dashed-table-tr alternate-rows">
                                    <Liabilities props={props} />
                                    {/* <sly data-sly-use.template="templates/liability_table.html" data-sly-call="${template.liability_table @model=model, properties=properties}" /> */}
                                  </div>
                                </div>
                              )}
                              {props.showNavEquity && activeTab === 4 && (
                                <div
                                  aria-expanded="false"
                                  aria-labelledby="equity_tab"
                                  className="tab-content tab4"
                                  id="equity_tab_panel"
                                  role="tabpanel"
                                >
                                  <div className="data-table dashed-table-tr alternate-rows">
                                    <Stakeholder props={props} />
                                    {/* <sly data-sly-use.template="templates/equity_table.html" data-sly-call="${template.equity_table @model=model, properties=properties}" /> */}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="d-none d-print-block w-100">
                              {financialSections.map((section, index) => (
                                <div key={index} className="mt-5">
                                  {section.component}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="d-none d-print-block w-100">
                    {financialSections.map((section, index) => (
                      <div key={index} className="mt-5">
                          {section.component}
                      </div>
                    ))}
                  </div>
                    {/* {model.showBodyInfo && !model.isEu &&  */}
                    <div>
                      <div className="module_body">
                        {/* <div data-sly-use.template="templates/revenue_chart_data.html" data-sly-call="${template.revenue_chart_data @model=model, properties=properties}" /> */}
                        <div />
                        <div
                          className="cmp-tabbed-nav financials-dropdown"
                          id="anchor-link-example"
                          role="menubar"
                        >
                          {/* <div data-sly-use.template="templates/subnav.html" data-sly-call="${template.subnav @model=model, properties=properties}" /> */}
                          <div />
                          <div className="tabbed-nav-content">
                            {/* tabindex="0" */}
                            {props.trendTitle && (
                              <div className="trend-title">
                                {props.trendTitle}
                              </div>
                            )}
                            {/* <div className="heading">{properties.model.showBodyInfo || !model.inlineMasking}</div> */}
                            {/* <div className="tab-content tab0" aria-expanded="true" aria-labelledby="annualIncomes_tab" id="annualIncomes_tab_panel" role="tabpanel" data-sly-test="${model.showNavIncome}">
                                            <div className="data-table dashed-table-tr alternate-rows">
                                                <div data-sly-test.showTable="${properties.use_income_table}">
                                                    <div data-sly-use.template="templates/revenue_table.html" data-sly-call="${template.revenue_table @model=model, properties=properties}" />
                                                </div>
                                                <div data-sly-test="${!showTable}">
                                                    <div data-sly-use.template="templates/revenue_chart.html" data-sly-call="${template.revenue_chart @model=model, properties=properties}" />
                                                </div>
                                            </div>
                                        </div>
                                        <div aria-expanded="false" aria-labelledby="cashflow_tab" className="tab-content tab1" id="cashflow_tab_panel" role="tabpanel" data-sly-test="${model.showNavCashflow}">
                                            <div className="data-table dashed-table-tr alternate-rows">
                                                <div data-sly-use.template="templates/cashflow_table.html" data-sly-call="${template.cashflow_table @model=model, properties=properties}" />
                                            </div>
                                        </div>
                                        <div aria-expanded="false" aria-labelledby="assets_tab" className="tab-content tab2" id="assets_tab_panel" role="tabpanel" data-sly-test="${model.showNavAssets}">
                                            <div className="data-table dashed-table-tr alternate-rows">
                                                <div data-sly-use.template="templates/assets_table.html" data-sly-call="${template.assets_table @model=model, properties=properties}" />
                                            </div>
                                        </div>
                                        <div aria-expanded="false" aria-labelledby="liability_tab" className="tab-content tab3" id="liability_tab_panel" role="tabpanel" data-sly-test="${model.showNavLiabilities}">
                                            <div className="data-table dashed-table-tr alternate-rows">
                                                <div data-sly-use.template="templates/liability_table.html" data-sly-call="${template.liability_table @model=model, properties=properties}" />
                                            </div>
                                        </div>
                                        <div aria-expanded="false" aria-labelledby="equity_tab" className="tab-content tab4" id="equity_tab_panel" role="tabpanel" data-sly-test="${model.showNavEquity}">
                                            <div className="data-table dashed-table-tr alternate-rows">
                                                <div data-sly-use.template="templates/equity_table.html" data-sly-call="${template.equity_table @model=model, properties=properties}" />
                                            </div>
                                        </div> */}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row overview pad-lr-0">
                      <div className="col-md-12">
                    <div
                      className="module_cta_financial mobile-cta-finacial-link"
                      style={{ position: "relative" }}
                    >
                      <a
                        href={
                          countryCode == "de" || countryCode == "at"
                            ? `${t(
                              "companyProfile.financial.getDandBCreditLink"
                            )}${dunsNumber}`
                            : t(
                              "companyProfile.financial.getDandBCreditLink"
                            )
                        }
                        aria-label="cta"
                        rel="noreferrer"
                        target={props?.cta?.newTab ? "_blank" : "_self"}
                      >
                        <span className="linkLabel">
                          {props?.cta?.type === "text" && (
                            <span>
                              <span className="icon right-unlock blue test"></span>{" "}
                              {t(
                                "companyProfile.financial.getDandBCreditLabel"
                              )}
                              <span className="icon chevron-right blue test"></span>
                            </span>
                          )}
                        </span>
                        {props?.cta?.type === "image" && (
                          <img
                            src={props.cta.srcUrl || ""}
                            alt={props.cta.altText || "altText"}
                          />
                        )}
                      </a>
                    </div>
                        </div>
                        </div>
                    {/* <div data-sly-include="jsInclude.jsp"/> */}
                    {/* <div className="footer module_cta" data-sly-test="${model.showHeaderInfo && properties.linkType}">
                            <div data-sly-use.template="templates/cta.html" data-sly-call="${template.cta @cta=model.cta}" />
                        </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Paper>
    </>
  );
};

export default Financial;
